import { Fragment, JSX, useMemo, useRef, useState } from 'react';
import { useRecordContext } from 'react-admin';
import { useFormContext } from "react-hook-form";
import Editor from '@monaco-editor/react';
import {
    Box,
    Button,
    Chip,
    Drawer,
    IconButton,
    Tooltip,
    Typography
} from '@mui/material';
import { useTheme, Theme } from '@mui/material/styles';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import { TemplateVariable, applyTemplateSamples } from './template-variables';

const MONO: string = '"Source Code Pro Regular", ui-monospace, SFMono-Regular, Menlo, monospace';

type Props = {
    /** One line saying what this template is for. */
    intro: string,
    variables: TemplateVariable[],
    /** Token -> sample value, rebuilt per preview so randomised data stays consistent. */
    buildSamples: () => Record<string, string>,
    /** Editor-specific DOMPurify configuration. */
    sanitize: (value: string) => string,
    height?: string,
};

/**
 * The HTML template editor used by the email and invoice setting types.
 *
 * Both editors previously opened with two columns of unstyled <p> tags — four
 * lines of instructions beside ten variable descriptions — pushing the editor
 * itself below the fold. The variables are the useful part, so they are chips
 * you can click to insert at the cursor, with the prose behind a toggle.
 */
export const TemplateEditor: (props: Props) => JSX.Element = (
    { intro, variables, buildSamples, sanitize, height = '520px' }: Props
): JSX.Element => {
    const record = useRecordContext();
    const { setValue } = useFormContext();
    const theme: Theme = useTheme();
    const editorRef = useRef<any>(null);

    const [html, setHtml] = useState<string>(record?.value ?? '');
    const [showPreview, setShowPreview] = useState<boolean>(false);
    const [showHelp, setShowHelp] = useState<boolean>(false);
    const [previewHtml, setPreviewHtml] = useState<string>('');

    // Templates in the wild use tokens this editor has no sample for — the
    // invite email uses {{invite_recipient}} and {{inviter_name}}. They used to
    // appear raw in the preview with no explanation. Say so instead of leaving
    // the author to wonder whether they had mistyped something.
    const unknownTokens: string[] = useMemo((): string[] => {
        const known: Set<string> = new Set(variables.map((v: TemplateVariable): string => v.token));
        const found: string[] = html.match(/\{\{\s*[a-zA-Z0-9_]+\s*\}\}/g) ?? [];
        const normalised: string[] = found.map((t: string): string => t.replace(/\s/g, ''));
        return Array.from(new Set(normalised))
            .filter((t: string): boolean => !known.has(t));
    }, [html, variables]);

    function handleEditorChange(value: string): void {
        const clean: string = sanitize(value ?? '');
        setHtml(clean);
        setValue('value', clean, { shouldDirty: true });
    }

    function handlePreviewToggle(): void {
        if (!showPreview) {
            setPreviewHtml(applyTemplateSamples(html, buildSamples()));
        }
        setShowPreview(!showPreview);
    }

    /** Drop a token in at the cursor rather than making people type it. */
    function insertVariable(token: string): void {
        const editor: any = editorRef.current;
        if (editor === null) {
            return;
        }
        editor.executeEdits('insert-template-variable', [{
            range: editor.getSelection(),
            text: token,
            forceMoveMarkers: true,
        }]);
        editor.focus();
    }

    return (
        <Box sx={{ width: '100%' }}>
            <Typography sx={{ fontSize: 13.5, color: 'text.secondary', lineHeight: 1.6 }}>
                { intro }
            </Typography>

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mt: 2.5,
                    mb: 1,
                }}
            >
                <Typography
                    component="h2"
                    sx={{
                        fontFamily: MONO,
                        fontSize: 10.5,
                        letterSpacing: '0.14em',
                        textTransform: 'uppercase',
                        color: 'text.secondary',
                    }}
                >
                    { 'Variables' }
                </Typography>
                <Typography sx={{ fontFamily: MONO, fontSize: 10.5, color: 'text.disabled' }}>
                    { variables.length }
                </Typography>

                <Button
                    size="small"
                    onClick={ (): void => setShowHelp(!showHelp) }
                    sx={{ ml: 'auto', textTransform: 'none', fontSize: 12.5 }}
                >
                    { showHelp ? 'Hide descriptions' : 'What do these mean?' }
                </Button>

                <Button
                    size="small"
                    variant="outlined"
                    startIcon={ <VisibilityIcon /> }
                    onClick={ handlePreviewToggle }
                    sx={{ textTransform: 'none', fontSize: 12.5 }}
                >
                    { 'Preview' }
                </Button>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                { variables.map((variable: TemplateVariable): JSX.Element => (
                    <Tooltip key={ variable.token } title={ variable.description } placement="top">
                        <Chip
                            size="small"
                            clickable
                            label={ variable.token }
                            onClick={ (): void => insertVariable(variable.token) }
                            sx={{
                                fontFamily: MONO,
                                fontSize: 11,
                                height: 24,
                                borderRadius: 1,
                            }}
                        />
                    </Tooltip>
                )) }
            </Box>

            { unknownTokens.length > 0 &&
                <Typography sx={{ mt: 1.25, fontSize: 12, color: 'text.secondary' }}>
                    { 'Also used here: ' }
                    <Box component="span" sx={{ fontFamily: MONO, color: 'warning.main' }}>
                        { unknownTokens.join('  ') }
                    </Box>
                    { '. This editor has no sample for those, so they are left as-is in the preview.' }
                </Typography>
            }

            { showHelp &&
                <Box
                    component="dl"
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'auto 1fr' },
                        columnGap: 2,
                        rowGap: 1,
                        mt: 2,
                        mb: 0,
                        p: 2,
                        borderRadius: 2,
                        border: 1,
                        borderColor: 'divider',
                        bgcolor: 'action.hover',
                    }}
                >
                    { variables.map((variable: TemplateVariable): JSX.Element => (
                        <Fragment key={ variable.token }>
                            <Box
                                component="dt"
                                sx={{ fontFamily: MONO, fontSize: 11.5, color: 'text.primary' }}
                            >
                                { variable.token }
                            </Box>
                            <Box
                                component="dd"
                                sx={{ m: 0, fontSize: 12.5, color: 'text.secondary' }}
                            >
                                { variable.description }
                            </Box>
                        </Fragment>
                    )) }
                </Box>
            }

            <Box
                sx={{
                    width: '100%',
                    height,
                    mt: 2.5,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2,
                    overflow: 'hidden',
                }}
            >
                <Editor
                    defaultValue={ html }
                    height="100%"
                    width="100%"
                    defaultLanguage="html"
                    theme={ theme.palette.mode === 'dark' ? 'vs-dark' : 'light' }
                    onMount={ (editor: any): void => { editorRef.current = editor; } }
                    onChange={ handleEditorChange }
                    options={{
                        minimap: { enabled: false },
                        fontSize: 13,
                        fontFamily: MONO,
                        wordWrap: 'on',
                        scrollBeyondLastLine: false,
                        automaticLayout: true,
                        padding: { top: 12, bottom: 12 },
                        renderLineHighlight: 'gutter',
                    }}
                />
            </Box>

            <Drawer anchor="right" open={ showPreview } onClose={ handlePreviewToggle }>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: { xs: '100vw', sm: 'min(920px, 96vw)' },
                        height: '100%',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            px: 2,
                            py: 1.5,
                            flex: '0 0 auto',
                            borderBottom: 1,
                            borderColor: 'divider',
                        }}
                    >
                        <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{ 'Preview' }</Typography>
                        <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                            { 'Variables filled in with sample data' }
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={ handlePreviewToggle }
                            aria-label="Close preview"
                            sx={{ ml: 'auto' }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    { /* Deliberately white in both themes: this is how the mail
                         renders in an inbox, so previewing it on a dark surface
                         would misrepresent it. */ }
                    <Box sx={{ flex: '1 1 auto', minHeight: 0, overflow: 'auto', bgcolor: '#fff', p: 2 }}>
                        <Box
                            sx={{ width: '100%', maxWidth: '100%', color: '#000' }}
                            dangerouslySetInnerHTML={{ __html: previewHtml }}
                        />
                    </Box>
                </Box>
            </Drawer>
        </Box>
    );
}
